import React, { useState } from 'react';
import { RoomStatus, type Room, type Tenant, type BoardingHouseInfo } from '../types';
import { ELECTRICITY_PRICE } from '../constants';

declare const html2canvas: any;
declare const jspdf: any;

interface RoomGridDashboardProps {
    rooms: Room[];
    setRooms: React.Dispatch<React.SetStateAction<Room[]>>;
    tenants: Tenant[];
    boardingHouseInfo: BoardingHouseInfo;
}


const numberToVnWords = (number: number): string => {
    if (number === 0) return "Không đồng";
    if (number > 999999999999999) return "Số quá lớn";

    const units = ["", "một", "hai", "ba", "bốn", "năm", "sáu", "bảy", "tám", "chín"];
    const teens = ["mười", "mười một", "mười hai", "mười ba", "mười bốn", "mười lăm", "mười sáu", "mười bảy", "mười tám", "mười chín"];
    const tens = ["", "mười", "hai mươi", "ba mươi", "bốn mươi", "năm mươi", "sáu mươi", "bảy mươi", "tám mươi", "chín mươi"];
    const thousands = ["", "nghìn", "triệu", "tỷ"];

    const convertGroup = (n: number): string => {
        let result = "";
        const hundred = Math.floor(n / 100);
        const ten = n % 100;
        
        if (hundred > 0) {
            result += units[hundred] + " trăm";
        }

        if (ten > 0) {
            if (hundred > 0) {
                result += ten < 10 ? " linh " : " ";
            }
            if (ten < 10) {
                result += units[ten];
            } else if (ten < 20) {
                result += teens[ten - 10];
            } else {
                result += tens[Math.floor(ten / 10)];
                const unit = ten % 10;
                if (unit > 0) {
                    if (unit === 1 && Math.floor(ten/10) > 1) {
                        result += " mốt";
                    } else if (unit === 5 && Math.floor(ten/10) > 0) {
                        result += " lăm";
                    } else if (unit === 4 && Math.floor(ten/10) > 1) {
                        result += " tư";
                    } else {
                        result += " " + units[unit];
                    }
                }
            }
        }
        return result;
    }

    const numStr = number.toString();
    const groups: string[] = [];
    for (let i = numStr.length; i > 0; i -= 3) {
        groups.push(numStr.substring(Math.max(0, i - 3), i));
    }

    const words: string[] = [];
    for (let i = 0; i < groups.length; i++) {
        const groupNum = parseInt(groups[i], 10);
        if (groupNum > 0) {
            let groupWord = convertGroup(groupNum);
            if (i > 0) {
                 groupWord += " " + thousands[i];
            }
            words.push(groupWord);
        }
    }
    
    let finalResult = words.reverse().join(" ");
    finalResult = finalResult.trim().replace(/\s+/g, ' ');
    finalResult = finalResult.charAt(0).toUpperCase() + finalResult.slice(1);

    return finalResult + " đồng chẵn.";
};


const InvoiceModal: React.FC<{ room: Room | null; tenantName: string; onClose: () => void; boardingHouseInfo: BoardingHouseInfo; }> = ({ room, tenantName, onClose, boardingHouseInfo }) => {
    const [isDownloading, setIsDownloading] = useState(false);
    if (!room) return null;

    const electricityUsage = room.currentElectricityReading - room.lastElectricityReading;
    const electricityCost = electricityUsage > 0 ? electricityUsage * ELECTRICITY_PRICE : 0;
    const totalBill = room.price + electricityCost;
    const today = new Date();
    const invoiceDate = `Ngày ${today.getDate()} tháng ${today.getMonth() + 1} năm ${today.getFullYear()}`;
    const totalBillInWords = numberToVnWords(totalBill);
    const invoiceId = `HD-${room.roomNumber}-${today.getFullYear()}${(today.getMonth() + 1).toString().padStart(2, '0')}`;


    const handlePrint = () => { window.print(); };

    const handleDownloadPdf = () => {
        const content = document.getElementById('invoice-content-to-download');
        if (!content) return;
        setIsDownloading(true);

        const { jsPDF } = jspdf;
        const pdf = new jsPDF({
            orientation: 'p',
            unit: 'mm',
            format: 'a4',
            hotfixes: ["px_scaling"],
        });
        
        // Embed the Noto Serif font to support Vietnamese characters
        if ((window as any).setupPdfFonts) {
            (window as any).setupPdfFonts(pdf);
        }
        pdf.setFont('Noto Serif', 'normal');

        pdf.html(content, {
            callback: (doc: any) => {
                doc.save(`Hoa-Don-Thang-${today.getMonth() + 1}-${room.roomNumber}-${tenantName.replace(/\s/g, '_')}.pdf`);
                setIsDownloading(false);
            },
            margin: [20, 20, 20, 20],
            autoPaging: 'text',
            width: 170,
            windowWidth: content.scrollWidth,
             html2canvas: {
                useCORS: true,
                scale: window.devicePixelRatio,
            },
            fontFaces: [
                {
                    family: 'Noto Serif',
                    style: 'normal',
                    weight: 'normal',
                    src: [{ url: 'NotoSerif-Regular.ttf', format: 'truetype' }]
                },
                {
                    family: 'Noto Serif',
                    style: 'normal',
                    weight: 'bold',
                    src: [{ url: 'NotoSerif-Bold.ttf', format: 'truetype' }]
                }
            ]
        }).catch((error: any) => {
            import('../utils/logger').then(l => l.error('Error generating PDF:', error));
            alert("Đã có lỗi xảy ra khi tạo file PDF.");
            setIsDownloading(false);
        });
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 no-print">
            <div id="invoice-modal" className="printable-content bg-white dark:bg-gray-800 p-8 rounded-lg shadow-2xl w-full max-w-3xl mx-auto text-gray-800 dark:text-gray-200 h-[90vh] overflow-y-auto">
                <div className="printable-document bg-white p-4">
                    <div id="invoice-content-to-download">
                        <div className="text-center mb-4">
                            <h3 className="font-bold uppercase">{boardingHouseInfo.name}</h3>
                            <p>Địa chỉ: {boardingHouseInfo.address}</p>
                            <p>ĐT: {boardingHouseInfo.phone}</p>
                        </div>
                        <div className="text-center my-6">
                            <h2 className="text-2xl font-bold">HÓA ĐƠN TIỀN NHÀ</h2>
                            <p>Tháng {today.getMonth() + 1} năm {today.getFullYear()}</p>
                            <p className="italic mt-1">Số: {invoiceId}</p>
                        </div>

                        <div className="mb-6">
                            <p><span className="font-semibold">Đơn vị thuê:</span> {tenantName}</p>
                            <p><span className="font-semibold">Phòng số:</span> {room.roomNumber}</p>
                        </div>

                        <table className="w-full border-collapse border border-gray-400">
                            <thead>
                                <tr className="">
                                    <th className="border border-gray-300 p-2 text-center font-semibold">STT</th>
                                    <th className="border border-gray-300 p-2 text-left font-semibold">Nội dung</th>
                                    <th className="border border-gray-300 p-2 text-right font-semibold">Thành tiền (VNĐ)</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td className="border border-gray-300 p-2 text-center">1</td>
                                    <td className="border border-gray-300 p-2">
                                        Tiền thuê phòng tháng {today.getMonth() + 1}/{today.getFullYear()}
                                        <p className="text-gray-500 italic">Bao gồm các chi phí dịch vụ cơ bản (nước, rác, internet).</p>
                                    </td>
                                    <td className="border border-gray-300 p-2 text-right">{room.price.toLocaleString()}</td>
                                </tr>
                                <tr>
                                    <td className="border border-gray-300 p-2 text-center">2</td>
                                    <td className="border border-gray-300 p-2">
                                        Tiền điện
                                        <p className="text-gray-500 italic">
                                            (Số mới: {room.currentElectricityReading} - Số cũ: {room.lastElectricityReading} = {electricityUsage} kWh) x {ELECTRICITY_PRICE.toLocaleString()} VNĐ/kWh
                                        </p>
                                    </td>
                                    <td className="border border-gray-300 p-2 text-right">{electricityCost.toLocaleString()}</td>
                                </tr>
                                <tr className="font-bold">
                                    <td colSpan={2} className="border border-gray-300 p-2 text-right">TỔNG CỘNG THANH TOÁN</td>
                                    <td className="border border-gray-300 p-2 text-right text-lg">{totalBill.toLocaleString()}</td>
                                </tr>
                            </tbody>
                        </table>
                        
                        <div className="mt-2">
                            <p><span className="font-semibold">Bằng chữ:</span> <span className="italic">{totalBillInWords}</span></p>
                        </div>

                        <div className="flex justify-around pt-12 text-center">
                            <div>
                                <h3 className="font-bold">Người nộp tiền</h3>
                                <p className="italic">(Ký và ghi rõ họ tên)</p>
                                <div className="h-24"></div>
                                <p className="font-semibold">{tenantName}</p>
                            </div>
                            <div>
                                <p className="italic mb-2">{invoiceDate}</p>
                                <h3 className="font-bold">Người lập phiếu</h3>
                                <p className="italic">(Ký và ghi rõ họ tên)</p>
                                <div className="h-24"></div>
                                <p className="font-semibold">{boardingHouseInfo.landlordName}</p>
                            </div>
                        </div>
                         <div className="text-center text-gray-500 pt-8 mt-8 border-t border-gray-300">
                            <p>© 2025 IVS JSC. Mọi quyền được bảo lưu.</p>
                            <p className="mt-1">Được thiết kế và phát triển bởi IVS Celestech, IVS JSC.</p>
                            <a href="https://ivsacademy.edu.vn/" target="_blank" rel="noopener noreferrer" className="mt-1 block text-primary-600 dark:text-primary-400 hover:underline">
                                https://ivsacademy.edu.vn/
                            </a>
                        </div>
                    </div>
                </div>
                <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3 sm:gap-0 sm:space-x-4 mt-8 no-print">
                    <button onClick={onClose} className="px-4 py-2 font-medium text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300 dark:bg-gray-600 dark:text-gray-200 dark:hover:bg-gray-500">Đóng</button>
                    <button
                        onClick={handleDownloadPdf}
                        disabled={isDownloading}
                        className="px-4 py-2 font-medium text-white bg-green-600 rounded-lg hover:bg-green-500 disabled:bg-green-300"
                    >
                        {isDownloading ? 'Đang xử lý...' : 'Tải PDF'}
                    </button>
                    <button onClick={handlePrint} className="px-4 py-2 font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-500">In Hóa Đơn</button>
                </div>
            </div>
        </div>
    );
};


const RoomGridDashboard: React.FC<RoomGridDashboardProps> = ({ rooms, setRooms, tenants, boardingHouseInfo }) => {
    const [isInvoiceModalOpen, setIsInvoiceModalOpen] = useState(false);
    const [selectedRoomForInvoice, setSelectedRoomForInvoice] = useState<Room | null>(null);

    const getTenantName = (tenantId?: string): string => tenants.find(t => t.id === tenantId)?.name || 'Trống';

    const handleElectricityChange = (roomId: string, value: string) => {
        const newReading = parseInt(value, 10);
        if (isNaN(newReading) || newReading < 0) return;

        setRooms(currentRooms => 
            currentRooms.map(r => 
                r.id === roomId ? { ...r, currentElectricityReading: newReading } : r
            )
        );
    };

    const handleOpenInvoice = (room: Room) => {
        setSelectedRoomForInvoice(room);
        setIsInvoiceModalOpen(true);
    };

    const RoomCard: React.FC<{ room: Room }> = ({ room }) => {
        const tenantName = getTenantName(room.tenantId);
        const statusInfo = {
            [RoomStatus.Available]: { text: 'Còn trống', color: 'text-green-800 dark:text-green-300', bg: 'bg-green-100 dark:bg-green-900/50'},
            [RoomStatus.Occupied]: { text: 'Đã thuê', color: 'text-blue-800 dark:text-blue-300', bg: 'bg-blue-100 dark:bg-blue-900/50'},
            [RoomStatus.Maintenance]: { text: 'Bảo trì', color: 'text-yellow-800 dark:text-yellow-300', bg: 'bg-yellow-100 dark:bg-yellow-900/50'},
        };
        const currentStatus = statusInfo[room.status];

        const electricityUsage = room.currentElectricityReading - room.lastElectricityReading;
        const electricityCost = electricityUsage > 0 ? electricityUsage * ELECTRICITY_PRICE : 0;
        const totalBill = room.price + electricityCost;

        return (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4 flex flex-col justify-between">
                <div>
                    <div className="flex justify-between items-start">
                        <p className="font-bold text-2xl text-gray-900 dark:text-white">P.{room.roomNumber}</p>
                        <span className={`px-2 py-1 text-xs font-semibold rounded-full ${currentStatus.color} ${currentStatus.bg}`}>
                            {currentStatus.text}
                        </span>
                    </div>
                    <div className="mt-2 text-sm">
                        <div className="flex justify-between items-center">
                            <span className="text-gray-500 dark:text-gray-400">Chủ phòng:</span>
                            <span className="font-medium text-gray-700 dark:text-gray-200 truncate">{tenantName}</span>
                        </div>
                    </div>
                </div>
                
                {room.status === RoomStatus.Occupied ? (
                    <div className="border-t border-gray-200 dark:border-gray-700 mt-3 pt-3 space-y-3">
                        <div>
                            <label className="text-xs font-medium text-gray-500 dark:text-gray-400">Số điện mới</label>
                            <input 
                                type="number" 
                                value={room.currentElectricityReading} 
                                onChange={(e) => handleElectricityChange(room.id, e.target.value)}
                                min={room.lastElectricityReading}
                                className="w-full mt-1 bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-primary-500 focus:border-primary-500 block p-2 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white"
                                onClick={(e) => e.stopPropagation()} // Prevent card click event
                            />
                        </div>
                         <div className="flex justify-between items-center text-sm">
                            <span className="text-gray-500 dark:text-gray-400">Tổng tháng này:</span>
                            <span className="font-bold text-base text-primary-600 dark:text-primary-400">{(totalBill).toLocaleString()} VNĐ</span>
                        </div>
                        <button 
                            onClick={(e) => { e.stopPropagation(); handleOpenInvoice(room); }} 
                            className="w-full px-3 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-500"
                        >
                            Xuất Hóa Đơn
                        </button>
                    </div>
                ) : (
                    <div className="border-t border-gray-200 dark:border-gray-700 mt-3 pt-3 text-center text-sm text-gray-400 italic">
                        {room.status === RoomStatus.Available ? 'Phòng trống' : 'Phòng đang bảo trì'}
                    </div>
                )}
            </div>
        );
    };

    return (
        <>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-5">
                {rooms.sort((a, b) => a.roomNumber.localeCompare(b.roomNumber)).map(room => (
                    <RoomCard key={room.id} room={room} />
                ))}
            </div>
            
            {isInvoiceModalOpen && <InvoiceModal room={selectedRoomForInvoice} tenantName={getTenantName(selectedRoomForInvoice?.tenantId)} onClose={() => setIsInvoiceModalOpen(false)} boardingHouseInfo={boardingHouseInfo} />}
        </>
    );
};

export default RoomGridDashboard;
